import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getDemoSlots } from '@/services/advertiseApi';

// Two-tier picker (day chips, then time buttons) backed by /demo/slots.
export function ScheduleSlotPicker({ track, value, onChange }) {
  const [slots, setSlots] = useState(null);
  const [activeDay, setActiveDay] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setSlots(null);
    getDemoSlots(track).then((data) => {
      if (cancelled) return;
      setSlots(data);
      setActiveDay(data[0]?.iso?.slice(0, 10) || null);
    });
    return () => { cancelled = true; };
  }, [track]);

  if (!slots) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" />Loading available times…
      </div>
    );
  }

  const days = [...new Set(slots.map((s) => s.iso.slice(0, 10)))];
  const dayLabel = (isoDate) => new Date(`${isoDate}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const timeLabel = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });

  return (
    <div>
      <div className="flex flex-wrap gap-2" data-testid="advertise-schedule-days">
        {days.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setActiveDay(d)}
            className={cn(
              'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
              activeDay === d ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-200 text-slate-700 hover:border-blue-300'
            )}
          >
            {dayLabel(d)}
          </button>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="advertise-schedule-times">
        {slots.filter((s) => s.iso.startsWith(activeDay)).map((s) => (
          <button
            key={s.iso}
            type="button"
            onClick={() => onChange(s)}
            className={cn(
              'rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
              value?.iso === s.iso ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-700 hover:border-blue-300'
            )}
          >
            {timeLabel(s.iso)} UTC
          </button>
        ))}
      </div>
    </div>
  );
}
